/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

// Startup point for the client side application

/* eslint-disable no-unused-vars */
import 'babel-polyfill';
import '../utils/Globals';
import https from 'https';
import Url from '../utils/Url';
window.cchome.util.setQueryParams();
const language = window.cchome.util.getStartupLanguage();
import AnalyticsUtil from '../utils/AnalyticsUtil';
import LocalizationService from './services/LocalizationService';
let IntlProvider;
let LoadTranslations: any = {};
import { getEnvironment } from '../utils/Environment';
import { DeviceDetectionUtil } from 'cchome-shared/utils';
import { CCHomeAppsUtil, productIdMap } from 'cchome-apps';

type RenderRoutesType = {
	app: any;
	profile: any;
};

window.ENV = getEnvironment();
function sendRedirectAnalytic(url, reason, errorDesc) {
	const analytics = new AnalyticsUtil(true);
	return analytics.sendRedirectAnalytic('redirect-ccd', url, reason, errorDesc);
}

function redirectCChomeUser(ingestPromise, redirectURL) {
	const timedOutRedirect = setTimeout(() => {
		window.location.href = redirectURL;
	}, 2000);
	if (ingestPromise && ingestPromise.then) {
		ingestPromise.then(() => {
			clearTimeout(timedOutRedirect);
			window.location.href = redirectURL;
		});
	}
}

if (!window.cchome.util.storageAvailable('cookie')) {
	const redirectURL = `${window.location.origin}/errors/cookieDisabled.html?ref=${encodeURIComponent(
		window.location.href,
	)}`;
	const ingestPromise = sendRedirectAnalytic(redirectURL, 'cookie-disabled', null);
	redirectCChomeUser(ingestPromise, redirectURL);
} else if (window.cchome.util.isUnsupportedIE()) {
	const redirectURL = 'https://www.adobe.com/go/cc_IE8_support'; // TODO: CCD - Convert to goURL
	const ingestPromise = sendRedirectAnalytic(redirectURL, 'unsupported-ie-browser', null); // TODO: CCD - Apply analytics
	redirectCChomeUser(ingestPromise, redirectURL);
} else if (window.cchome.util.isUnsupportedIE11()) {
	const redirectURL = `${window.location.origin}/errors/unsupportedBrowser.html?ref=${encodeURIComponent(
		window.location.href,
	)}`;
	const ingestPromise = sendRedirectAnalytic(redirectURL, 'unsupported-ie-browser', null);
	redirectCChomeUser(ingestPromise, redirectURL);
} else {
	// redirect to /apps/updates on creative-cloud campaign page for cc-june-2020-release
	const path = window.location.pathname.split('/');
	if (
		!DeviceDetectionUtil.isMobileOrTab() &&
		path[4] === 'campaign' &&
		path[3] === 'creative-cloud' &&
		Url.getParam('id') === '2020-06-cc-release'
	) {
		window.location.href = `/apps/updates${window.location.search}`;
	}
	// TODO: CCD - Need to apply workaround for SUSI adding # at the end of the URL
	// We only load these if the browser supports
	LoadTranslations = LocalizationService.loadTranslations(language);
	IntlProvider = require('react-intl').IntlProvider;
}

import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import reducers from './reducers';
import TransitionScreen from './containers/TransitionScreen/TransitionScreen';
import ErrorBoundary from './containers/ErrorBoundary/ErrorBoundary';
import SpectrumProvider from '@react/react-spectrum/Provider';

/* eslint-enable no-unused-vars */

import ReactDOM from 'react-dom';
import { createStore, applyMiddleware } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { renderRoutes } from 'react-router-config';
import { SET_BOOTSTRAP_COMPLETED, SET_LANGUAGE, FETCH_CURRENT_USER } from './types';
import getConfig from '../config';
import BootstrapService from './services/BootstrapService';
import G from 'glob';

const rootEl = document.querySelector('#root');
const loaderEl = document.querySelector('#loader');
if (!(rootEl instanceof Element) || !(loaderEl instanceof Element)) {
	throw new Error('invalid type');
}

const createStoreWithMiddleware = applyMiddleware(thunkMiddleware)(createStore);
const store = createStoreWithMiddleware(reducers, {});

// dynamically load the GDPR script
function loadDTMScript() {
	window.addEventListener('adobePrivacy:PrivacyConsent', () => {
		window.privacyConsentEventTriggered = true;
		if (window.isCCDMainMounted) {
			const event = new CustomEvent('privacyConsentEventTriggered');
			document.dispatchEvent(event);
		}
	});
	if (!window.cchome.util.hasFeature('disable_launch')) {
		let notice;
		// marketingtech is the adobe launch flag to check if we are loading the
		// GDPR script.
		let environment = 'production';

		switch (getEnvironment()) {
			case 'prod':
				environment = 'production';
				break;

			case 'stage':
				environment = 'stage';
				break;

			case 'local':
			case 'dev':
				environment = 'dev';
				break;

			default:
				environment = 'production';
				break;
		}

		const script = document.createElement('script');
		script.src = 'https://www.adobe.com/etc/beagle/public/globalnav/adobe-privacy/latest/privacy.min.js';

		script.async = true;
		document.body ? document.body.appendChild(script) : '';

		window.marketingtech.adobe = {
			audienceManager: true,
			launch: {
				property: 'global',
				environment,
			},
		};

		// require the adobe launch library
		require('@marketingtech/launch-code/dist/main.min.js');

		window.fedsConfig = window.fedsConfig || {};
		window.fedsConfig.privacy = window.fedsConfig.privacy || {};
		window.fedsConfig.privacy.otDomainId = '7a5eb705-95ed-4cc4-a11d-0cc5760e93db-test';
		if (environment === 'production') {
			window.fedsConfig.privacy.otDomainId = '7a5eb705-95ed-4cc4-a11d-0cc5760e93db';
		}
		window.fedsConfig.privacy.footerLinkSelector =
			'.footer-container .consent-link-hidden, .trailhead-footer-container .consent-link-hidden';
	}
}

store.dispatch({
	type: SET_LANGUAGE,
	langCookie: language,
});

if (LoadTranslations?.then) {
	LoadTranslations.then((localizedStrings) => {
		// Show the transition screen. On the first visit, this should show for a minimum of 5 seconds.
		// For repeat visits, this should dismiss as soon as the Bootstrap process completes.
		ReactDOM.render(
			<Provider store={store}>
				<IntlProvider locale={language} messages={localizedStrings}>
					<TransitionScreen isCCD={true} />
				</IntlProvider>
			</Provider>,
			loaderEl,
		);

		BootstrapService(null, store, true) // eslint-disable-line new-cap
			.then((initialState) => {
				// Bootstrap the application as soon as the services are ready.
				if (initialState) {
					store.dispatch({
						type: SET_BOOTSTRAP_COMPLETED,
						payload: true,
					});
					store.dispatch({
						type: FETCH_CURRENT_USER,
						payload: initialState,
					});

					// Redirection for CC Home Apps Urls in case of productId changes
					CCHomeAppsUtil.redirectToNewProductId();

					// invoke the loadDTMScript method
					window.marketingtech = window.marketingtech || {};
					loadDTMScript();

					const typekitId = getConfig('SUPPORTED_LANGUAGES')[language].typekitId;

					const renderRoutesObject: RenderRoutesType = {
						app: store.getState().app,
						profile: store.getState().auth.profile,
					};

					ReactDOM.render(
						<Provider store={store}>
							{/* $FlowFixMe */}
							<IntlProvider locale={language} messages={localizedStrings}>
								<ErrorBoundary locale={language}>
									<BrowserRouter>
										<SpectrumProvider theme="light" typekitId={typekitId}>
											{renderRoutes(require('./Routes/LoggedOut/MainRoutes').default, {
												app: store.getState().app,
												profile: store.getState().auth.profile,
											})}
										</SpectrumProvider>
									</BrowserRouter>
								</ErrorBoundary>
							</IntlProvider>
						</Provider>,
						rootEl,
					);

					window.cchome.util.logger.info('Home', 'Loaded CCHome Downloads Successfully');
				}
			})
			.catch((e) => {
				// redirect to 5xx page
				console.log(e);
				const currentURl = window.location.origin;
				const rediectURL = `${currentURl}/errors/5XX.html`;
				const ingestPromise = sendRedirectAnalytic(rediectURL, 'ccd-bootstrap-failed', e);
				redirectCChomeUser(ingestPromise, rediectURL);
				store.dispatch({
					type: SET_BOOTSTRAP_COMPLETED,
					payload: true,
				});
			});
	}).catch((e) => {
		// redirect to 5xx page
		window.cchome.util.logger.error('Client', 'Error loading application', e);
		const currentURl = window.location.origin;
		const rediectURL = `${currentURl}/errors/5XX.html`;
		const ingestPromise = sendRedirectAnalytic(rediectURL, 'ccd-load-translations-failed', e);
		redirectCChomeUser(ingestPromise, rediectURL);
		store.dispatch({
			type: SET_BOOTSTRAP_COMPLETED,
			payload: true,
		});
	});
}
